<template>
  <v-container fluid class="pa-0 bg-slate-50 min-h-screen" style="font-family: 'Inter', sans-serif;">
    <div class="p-4">
    <div class="max-w-7xl mx-auto space-y-8">
      
      <!-- HEADER -->
      <div class="mb-6 flex items-center justify-between">
        <div>
          <button @click="$router.back()" class="flex items-center gap-2 text-slate-400 hover:text-orange-600 transition-colors mb-2">
            <v-icon small color="inherit">mdi-arrow-left</v-icon>
            <span class="text-[10px] font-bold uppercase tracking-wider">Voltar</span>
          </button>
          <h1 class="text-2xl font-bold text-slate-700">Gerenciar SubSetores</h1>
          <p class="text-sm text-slate-500">Administre os subsetores vinculados à secretaria selecionada</p>
        </div>
      </div>

      <!-- FORMULÁRIO DE CADASTRO/EDIÇÃO (CARD) -->
      <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
        <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
          <v-icon color="orange" small>{{ subSecData.subsec_id ? 'mdi-pencil' : 'mdi-plus-circle' }}</v-icon>
          <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">
            {{ subSecData.subsec_id ? 'EDITAR SUBSETOR' : 'NOVO SUBSETOR' }}
          </h2>
        </div>
        
        <div class="p-8">
          <form @submit.prevent="createOrUpdate" class="flex flex-col md:flex-row items-end gap-6">
            <div class="flex-1 w-full">
              <label class="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Nome do SubSetor</label>
              <input 
                v-model="subSecData.subsec_desc"
                ref="secName"
                placeholder="EX: FINANCEIRO / RECEPÇÃO"
                class="w-full bg-slate-50 border border-slate-200 rounded px-4 py-3 text-sm font-bold text-slate-700 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 transition-all uppercase placeholder:text-slate-300"
              />
              <p v-if="subSecData.subsec_id" class="text-[9px] text-orange-500 font-bold uppercase mt-1.5 ml-1 flex items-center gap-1">
                <v-icon x-small color="orange">mdi-information-outline</v-icon>
                Editando registro existente
              </p>
            </div>
            
            <div class="flex gap-3 w-full md:w-auto">
              <button 
                v-if="subSecData.subsec_id"
                type="button"
                @click="newData"
                class="bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold text-[10px] px-8 py-3.5 rounded transition-all uppercase tracking-widest border border-slate-200 flex-1 md:flex-none"
              >
                Cancelar
              </button>
              <button 
                type="submit"
                :disabled="loader"
                class="bg-orange-500 hover:bg-orange-600 text-white font-bold text-[10px] px-8 py-3.5 rounded shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all uppercase tracking-widest flex items-center justify-center gap-2 flex-1 md:flex-none"
              >
                <v-icon v-if="!loader" small color="white">mdi-check</v-icon>
                <span v-else class="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></span>
                {{ subSecData.subsec_id ? 'Atualizar' : 'Salvar Registro' }}
              </button>
            </div>
          </form>
        </div>
      </section>

      <!-- LISTAGEM -->
      <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <!-- BARRA DE FERRAMENTAS DA TABELA -->
          <div class="px-6 py-4 flex items-center justify-between" style="background-color: #333333;">
              <div class="flex items-center gap-3">
                <v-icon color="orange" small>mdi-format-list-bulleted</v-icon>
                <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">SUBSETORES CADASTRADOS</h2>
              </div>
              
              <div class="flex items-center gap-4 flex-1 justify-end text-white">
                <div class="relative w-80">
                  <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <v-icon color="white" small class="opacity-50">mdi-magnify</v-icon>
                  </div>
                  <input 
                    v-model="search"
                    placeholder="PESQUISAR SUBSETOR..."
                    class="bg-white/10 border border-white/20 rounded-lg py-2 pl-10 pr-4 text-[11px] text-white placeholder:text-white/40 focus:outline-none focus:bg-white/20 transition-all w-full uppercase tracking-widest"
                  >
                </div>
              </div>
          </div>

          <div class="p-0">
              <v-data-table
                :headers="headers"
                :items="data"
                :search="search"
                :page.sync="page"
                :items-per-page="itemsPerPage"
                @page-count="pageCount = $event"
                :loading="loader"
                loading-text="CARREGANDO..."
                class="custom-table"
                hide-default-footer
              >
                <template v-slot:item.subsec_desc="{ item }">
                  <span class="text-sm font-bold text-slate-700 uppercase tracking-wide">{{ item.subsec_desc }}</span>
                </template>

                <template v-slot:item.sec_name="{ item }">
                  <span class="text-[11px] font-bold text-slate-400 tracking-wider uppercase">{{ item.sec_name }}</span>
                </template>

                <template v-slot:item.action="{ item }">
                  <div class="flex justify-end gap-2 px-2 py-2">
                    <button 
                      @click="loadData(item)"
                      class="p-2 hover:bg-orange-50 text-orange-600 rounded-lg transition-colors"
                      title="Editar"
                    >
                      <v-icon small color="orange">mdi-pencil</v-icon>
                    </button>
                    <button 
                      @click="deleteItem(item.subsec_id)"
                      class="p-2 hover:bg-red-50 text-red-600 rounded-lg transition-colors"
                      title="Excluir"
                    >
                      <v-icon small color="red">mdi-delete</v-icon>
                    </button>
                  </div>
                </template>

                <template v-slot:no-data>
                  <div class="py-12 flex flex-col items-center justify-center text-slate-400">
                    <v-icon size="48" color="slate-200">mdi-database-off</v-icon>
                    <p class="mt-2 text-sm">Nenhum subsetor encontrado</p>
                  </div>
                </template>
              </v-data-table>

              <!-- Custom Pagination Footer -->
              <div class="bg-slate-50 px-6 py-8 border-t border-slate-200 flex flex-col items-center justify-center gap-6 text-[10px] text-slate-500 font-bold uppercase tracking-widest w-full">
                  <span>Exibindo {{ (page - 1) * itemsPerPage + 1 }} de {{ data.length }} registros</span>
                  
                  <div class="flex gap-1" v-if="pageCount > 1">
                      <button 
                        @click="page > 1 ? page-- : null" 
                        :disabled="page === 1"
                        class="px-3 py-1.5 bg-white border border-slate-200 rounded-md hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Anterior
                      </button>
                      
                      <button 
                        v-for="p in visiblePages" 
                        :key="p"
                        @click="page = p"
                        :class="p === page ? 'bg-slate-700 text-white border-slate-700 shadow-sm' : 'bg-white border-slate-200 hover:bg-slate-100'"
                        class="px-3 py-1.5 border rounded-md transition-colors"
                      >
                        {{ p }}
                      </button>

                      <button 
                        @click="page < pageCount ? page++ : null" 
                        :disabled="page === pageCount"
                        class="px-3 py-1.5 bg-white border border-slate-200 rounded-md hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Próximo
                      </button>
                  </div>
              </div>
          </div>
      </section>

      <!-- NOTIFICAÇÕES -->
      <v-snackbar
        v-model="snack.show"
        :color="snack.color"
        timeout="3000"
        rounded="lg"
      >
        <div class="flex items-center gap-3">
          <v-icon color="white" small>
            {{ snack.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}
          </v-icon>
          <span class="text-xs font-bold uppercase tracking-wider">{{ snack.message }}</span>
        </div>
      </v-snackbar>
    </div>
    </div>
  </v-container>
</template>

<script>
export default {
  props: ["id"],
  data() {
    return {
      search: "",
      loader: true,
      data: [],
      subSecData: {
        subsec_desc: "",
        subsec_id: null,
        sec_id: this.id
      },
      snack: {
        show: false,
        message: "",
        color: ""
      },
      headers: [
        { text: "Nome do SubSetor", value: "subsec_desc" },
        { text: "Secretaria Vinculada", value: "sec_name" },
        { text: "Ações", align: "right", value: "action", sortable: false }
      ],
      page: 1,
      pageCount: 0,
      itemsPerPage: 10,
    };
  },
  computed: {
    visiblePages() {
      const pages = [];
      const totalVisible = 5;
      let start = Math.max(1, this.page - Math.floor(totalVisible / 2));
      let end = Math.min(this.pageCount, start + totalVisible - 1);
      if (end - start + 1 < totalVisible) start = Math.max(1, end - totalVisible + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    initialize() {
      this.loader = true;
      this.data = [];
      this.$store.dispatch("setLoader", this.loader);
      this.$http
        .get(`/prefectures/subsectors/${this.id}`)
        .then(r => {
          this.data = r.data;
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
        })
        .catch(e => {
          this.loader = false;
          this.snack.show = true;
          this.snack.message = e;
          this.snack.color = "error";
          this.$store.dispatch("setLoader", this.loader);
        });
    },
    newData() {
      this.subSecData.subsec_desc = "";
      this.subSecData.subsec_id = null;
      if (this.$refs.secName) this.$refs.secName.focus();
    },
    loadData(data) {
      this.subSecData.subsec_desc = data.subsec_desc;
      this.subSecData.subsec_id = data.subsec_id;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    createOrUpdate() {
      if (!this.subSecData.subsec_desc) {
          this.snack.show = true;
          this.snack.message = "Informe o nome do subsetor";
          this.snack.color = "warning";
          return;
      }

      this.loader = true;
      this.$store.dispatch("setLoader", true);

      const method = this.subSecData.subsec_id ? 'put' : 'post';
      const url = this.subSecData.subsec_id 
        ? `/prefectures/subsectors/${this.subSecData.subsec_id}` 
        : `/prefectures/subsectors/${this.id}`;

      this.$http[method](url, this.subSecData)
        .then(r => {
          this.snack.show = true;
          this.snack.message = r.data.result;
          this.snack.color = "success";
          this.initialize();
          this.newData();
        })
        .catch(e => {
          this.snack.show = true;
          this.snack.message = e.message;
          this.snack.color = "error";
          this.loader = false;
          this.$store.dispatch("setLoader", false);
        });
    },
    deleteItem(id) {
      let c = confirm("Deseja excluir o registro?");
      if (c) {
        this.$store.dispatch("setLoader", true);
        this.$http
          .delete(`/prefectures/subsectors/${id}`)
          .then(r => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.snack.color = "success";
            this.initialize();
          })
          .catch(e => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.$store.dispatch("setLoader", false);
          });
      }
    }
  }
};
</script>

<style scoped>
.custom-table ::v-deep table { background-color: transparent !important; border-collapse: separate; border-spacing: 0; }
.custom-table ::v-deep th { 
  color: #64748b !important; 
  font-weight: 700 !important; 
  text-transform: uppercase !important; 
  font-size: 0.75rem !important; 
  background-color: #f8fafc !important;
  border-bottom: 1px solid #e2e8f0 !important;
  height: 48px !important;
  padding: 0 24px !important;
}
.custom-table ::v-deep td { 
  border-bottom: 1px solid #f1f5f9 !important;
  color: #334155 !important;
  font-size: 0.875rem !important;
  padding: 12px 24px !important;
}
.custom-table ::v-deep tr:hover {
  background-color: #fcfcfc !important;
}
</style>